import { Lock } from "lucide-react";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Badge } from "@/components/atoms/Badge";
import type { ScoringRule } from "@/modules/leagues/types/league";

interface ScoringRulesEditorProps {
  rules: ScoringRule[];
  onChange?: (rules: ScoringRule[]) => void;
  readOnly?: boolean;
  frozenAt?: string | null;
}

/**
 * Editor de regras de pontuação. Usado em dois contextos:
 * 1) Wizard de criação (editável — rascunho antes da liga existir)
 * 2) Configurações > Pontuação (somente leitura — regras já CONGELADAS
 *    no momento da criação, conforme Addendum "Liga como Hub Central" §2.1)
 */
export function ScoringRulesEditor({ rules, onChange, readOnly = false, frozenAt }: ScoringRulesEditorProps) {
  function updatePoints(id: string, points: number) {
    if (!onChange) return;
    onChange(rules.map((r) => (r.id === id ? { ...r, points } : r)));
  }

  return (
    <div className="space-y-3">
      {readOnly && frozenAt && (
        <div className="flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Regras congeladas em {new Date(frozenAt).toLocaleDateString("pt-BR")} — não podem mais ser alteradas.
          Use "Importar Pontos" para ajustes manuais pontuais.
        </div>
      )}

      <div className="space-y-2.5">
        {rules.map((rule) => (
          <div key={rule.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
            <Label htmlFor={`rule-${rule.id}`} className="flex-1 text-sm font-medium">
              {rule.label}
            </Label>
            {readOnly ? (
              <Badge variant="primary">{rule.points} pts</Badge>
            ) : (
              <Input
                id={`rule-${rule.id}`}
                type="number"
                min={0}
                value={rule.points}
                onChange={(e) => updatePoints(rule.id, Number(e.target.value))}
                className="h-9 w-20 text-center"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
